import { useState, useRef, useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { invoicesAPI, projectsAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Upload, Camera, Loader2, X, Image, FileText } from 'lucide-react'

interface InvoiceUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (invoice: any) => void
}

export function InvoiceUploadDialog({
  open,
  onOpenChange,
  onSuccess,
}: InvoiceUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [type, setType] = useState<'OBRA' | 'OPERACIONAL'>('OBRA')
  const [projectId, setProjectId] = useState<string>('')

  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsAPI.list(),
    enabled: open,
  })

  const projects = (projectsData as any)?.data ?? (projectsData as any)?.projects ?? []

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => invoicesAPI.upload(formData),
    onSuccess: (data: any) => {
      toast.success('Nota fiscal enviada! Processando OCR...')
      onSuccess(data)
      handleClose()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleClose = useCallback(() => {
    setSelectedFile(null)
    if (preview) {
      URL.revokeObjectURL(preview)
    }
    setPreview(null)
    setIsDragOver(false)
    setType('OBRA')
    setProjectId('')
    onOpenChange(false)
  }, [preview, onOpenChange])

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file)
    const objectUrl = URL.createObjectURL(file)
    setPreview(objectUrl)
  }, [])

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null)
    if (preview) {
      URL.revokeObjectURL(preview)
    }
    setPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [preview])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect]
  )

  const handleSubmit = useCallback(() => {
    if (!selectedFile) return

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('type', type)
    if (type === 'OBRA' && projectId) {
      formData.append('projectId', projectId)
    }

    uploadMutation.mutate(formData)
  }, [selectedFile, type, projectId, uploadMutation])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar Nota Fiscal</DialogTitle>
          <DialogDescription>
            Envie uma foto ou PDF da nota fiscal para processamento automático via OCR.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Area */}
          {!selectedFile ? (
            <div
              className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-neutral-300 hover:border-neutral-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 text-neutral-400 mb-3" />
              <p className="text-sm text-neutral-600 text-center">
                Arraste a foto ou PDF da nota fiscal ou clique para selecionar
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                capture="environment"
                className="hidden"
                onChange={handleInputChange}
              />
            </div>
          ) : (
            <div className="relative rounded-lg border overflow-hidden">
              {selectedFile?.type === 'application/pdf' ? (
                <div className="flex flex-col items-center justify-center bg-neutral-50 p-8 min-h-64">
                  <FileText className="h-16 w-16 text-neutral-400 mb-3" />
                  <p className="text-sm text-neutral-600">{selectedFile.name}</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <img
                  src={preview!}
                  alt="Preview da nota fiscal"
                  className="w-full max-h-64 object-contain bg-neutral-50"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={handleRemoveFile}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Camera button for mobile */}
          {!selectedFile && (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:hidden"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="mr-2 h-4 w-4" />
              Tirar Foto
            </Button>
          )}

          {/* Type Selector */}
          <div>
            <Label>Tipo</Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as 'OBRA' | 'OPERACIONAL')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OBRA">Obra</SelectItem>
                <SelectItem value="OPERACIONAL">Operacional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Project Selector - only when type is OBRA */}
          {type === 'OBRA' && (
            <div>
              <Label>Projeto</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o projeto (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(projects) &&
                    projects.map((project: any) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!selectedFile || uploadMutation.isPending}
            onClick={handleSubmit}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Image className="mr-2 h-4 w-4" />
            )}
            Enviar e Processar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
